import {
  DocumentReference,
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  orderBy,
  query,
  startAfter,
  writeBatch,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IAiConversationMessage,
  IAiConversationMessageRaw,
  IAiConversationReactionsReport,
  IAiConversationReactionsReportFulfilled,
  IAiConversationReactionsReportRaw,
  IFirebasePaginationOptions,
  IGenericError,
} from '../types'

const aiConversationReactionsReportsCollectionName =
  'aiConversationReactionsReports'
const messagesSubCollectionName = 'messages'

const aiConversationReactionsReportDocRef = (id: string) =>
  doc(firebaseDb, aiConversationReactionsReportsCollectionName, id)

const aiConversationReactionsReportsCollectionRef = collection(
  firebaseDb,
  aiConversationReactionsReportsCollectionName,
)

export async function getAiConversationReactionsReportById(
  id: string,
): Promise<IAiConversationReactionsReport | undefined> {
  try {
    const docRef = aiConversationReactionsReportDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as unknown as DocumentSnapshot<IAiConversationReactionsReportRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IAiConversationReactionsReport = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while tying to get aiConversation Reaction Report by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
    )
  }
}

export async function getAiConversationReactionReports(
  queryConstraints: QueryConstraint[] = [],
): Promise<IAiConversationReactionsReport[]> {
  try {
    const q = query(
      aiConversationReactionsReportsCollectionRef,
      ...queryConstraints,
    )
    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IAiConversationReactionsReportRaw>

    let documents: IAiConversationReactionsReport[] = []

    querySnapshot.forEach((snapshot) => {
      documents.push({
        id: snapshot.id,
        ...snapshot.data(),
      })
    })

    return documents
  } catch (error: unknown) {
    console.error(
      `error while trying to get aiConversation Reaction Reports :>> `,
      (error as IGenericError)?.message || error,
      queryConstraints,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get conversation Reaction Reports`,
    )
  }
}

export async function getAiConversationReactionReportsPaginated(
  options: IFirebasePaginationOptions = {},
): Promise<{ reports: IAiConversationReactionsReport[]; total: number }> {
  try {
    const {
      page = 1,
      pageItemsLimit = 10,
      sortBy = 'updatedAt',
      sortDirection = 'desc',
    } = options

    // Build query constraints
    const queryConstraints: QueryConstraint[] = [
      orderBy(sortBy, sortDirection),
      limit(pageItemsLimit),
    ]

    // If not the first page, get the starting point
    if (page > 1) {
      // Get documents for the previous page to find the starting point
      const prevPageQuery = query(
        aiConversationReactionsReportsCollectionRef,
        orderBy(sortBy, sortDirection),
        limit((page - 1) * pageItemsLimit),
      )

      const prevPageSnapshot = await getDocs(prevPageQuery)
      const lastVisible =
        prevPageSnapshot.docs[prevPageSnapshot.docs.length - 1]

      if (lastVisible) {
        queryConstraints.push(startAfter(lastVisible))
      }
    }

    // Execute the main query
    const q = query(
      aiConversationReactionsReportsCollectionRef,
      ...queryConstraints,
    )
    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IAiConversationReactionsReportRaw>

    // Get total count
    const countSnapshot = await getCountFromServer(
      aiConversationReactionsReportsCollectionRef,
    )
    const total = countSnapshot.data().count

    // Process the documents
    const reports: IAiConversationReactionsReport[] = []
    querySnapshot.forEach((snapshot) => {
      reports.push({
        id: snapshot.id,
        ...snapshot.data(),
      })
    })

    return { reports, total }
  } catch (error: unknown) {
    console.error(
      `error while trying to get aiConversation Reaction Reports :>> `,
      (error as IGenericError)?.message || error,
      options,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get conversation Reaction Reports`,
    )
  }
}

export async function getAiConversationReactionReportWithMessagesById(
  id: string,
): Promise<IAiConversationReactionsReportFulfilled> {
  try {
    const report = await getAiConversationReactionsReportById(id)

    if (!report) {
      throw new Error('Conversation Reactions Report not found')
    }

    const messagesRef = collection(
      firebaseDb,
      `${aiConversationReactionsReportsCollectionName}/${report.id}/${messagesSubCollectionName}`,
    )

    const messagesQuery = query(messagesRef, orderBy('createdAt', 'asc'))

    const messagesSnap = (await getDocs(
      messagesQuery,
    )) as unknown as QuerySnapshot<IAiConversationMessageRaw>

    const messages: IAiConversationMessage[] = await Promise.all(
      messagesSnap.docs.map(async (messageDoc) => {
        const messageDocData = messageDoc.data()

        const message: IAiConversationMessage = {
          id: messageDoc.id,
          ...messageDocData,
        }

        return message
      }),
    )

    return { ...report, messages }
  } catch (error: unknown) {
    console.error(
      `error while trying to get ai conversation reaction report with messages by id: ${id} :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get conversation reaction report with messages`,
    )
  }
}

export async function deleteAiConversationReactionsReport(
  reportId: string,
): Promise<void> {
  try {
    const docRef = aiConversationReactionsReportDocRef(reportId)

    // first delete all messages in the sub collection
    await deleteAiConversationReactionsReportMessagesSubCollection(docRef)

    // then delete the parent conversation reaction report document
    await deleteDoc(docRef)
  } catch (error: unknown) {
    console.error(
      `error while trying to delete aiConversation reactions report :>> `,
      {
        message: (error as IGenericError)?.message,
        error,
      },
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete conversation reactions report`,
    )
  }
}

// deleteAiConversationReactionsReport helper for sub collection removal
async function deleteAiConversationReactionsReportMessagesSubCollection(
  parentDocRef: DocumentReference,
): Promise<void> {
  const messagesRef = collection(parentDocRef, messagesSubCollectionName)

  // maximum batch size for Firestore is 500
  const batchSize = 250
  let hasMoreMessages = true

  while (hasMoreMessages) {
    // Get a batch of documents
    const messagesSnapshot = await getDocs(query(messagesRef, limit(batchSize)))

    // if no documents left, we're done here
    if (messagesSnapshot.empty) {
      hasMoreMessages = false
      continue
    }

    // delete documents in batch
    const batch = writeBatch(getFirestore())
    messagesSnapshot.forEach((doc) => {
      batch.delete(doc.ref)
    })

    await batch.commit()

    // if we got less documents than the batch size, we're done here
    if (messagesSnapshot.size < batchSize) {
      hasMoreMessages = false
    }
  }
}
